import type { WorldObjectDef } from '@/world-prototype/objects/placeObject';

/**
 * Declarative Holly Springs (raleigh) backyard prop pack.
 *
 * Reuses shared/jacksonville prop sprites — layouts are independent of Hubert.
 * Layouts: `layouts.desktop` / `layouts.tablet` / `layouts.mobile`. Flat fields
 * mirror desktop. Updated by World Editor (?edit=1).
 */
const PROP = `${import.meta.env.BASE_URL}art/environments/jacksonville/backyard/props`;
const SHARED_PROP = `${import.meta.env.BASE_URL}art/environments/shared/props`;

export const RALEIGH_PROP_DEFS: readonly WorldObjectDef[] = [
  {
    id: "garden-bed",
    title: "Garden",
    icon: "🌸",
    actionLabel: "Look",
    shape: "garden",
    enabled: true,
    x: 0.20392,
    y: 0.67621,
    widthFrac: 0.0537,
    layouts: {
      desktop: {
        x: 0.20392,
        y: 0.67621,
        widthFrac: 0.0537,
        scale: 1.065,
      },
      tablet: {
        x: 0.2172,
        y: 0.60745,
        widthFrac: 0.12,
      },
      mobile: {
        x: 0.20515,
        y: 0.61507,
        widthFrac: 0.1006,
      },
    },
    gardenType: "flowers",
    sprite: {
      src: `${PROP}/garden-bed-v1.png`,
      alt: "Raised garden with corn and tomatoes",
      imageWidth: 1461,
      imageHeight: 776,
    },
    spriteVariants: {
      vegetables: {
        src: `${PROP}/garden-bed-v1.png`,
        alt: "Raised garden with corn and tomatoes",
        imageWidth: 1461,
        imageHeight: 776,
      },
      flowers: {
        src: `${PROP}/garden-bed-flowers-v1.png`,
        alt: "Raised flower garden bed",
        imageWidth: 1460,
        imageHeight: 826,
      },
    },
    footprint: [
      { x: -0.038, y: 0.007 },
      { x: 0.038, y: 0.007 },
      { x: 0.034, y: -0.018 },
      { x: -0.034, y: -0.018 },
    ],
    stand: { x: 0.03, y: 0.023 },
    hit: { x: 0.015, y: -0.058 },
    hitRadius: 0.026,
    activationZone: {
      shape: "circle",
      x: 0.03,
      y: 0.023,
      radius: 0.016,
    },
    ambient: "leaf-sway",
    groundShadow: "soft",
  },
  {
    id: "little-pine-tree",
    title: "Pine Tree",
    icon: "🌲",
    actionLabel: "Look",
    shape: "hammock",
    interactive: false,
    enabled: true,
    x: 0.7533,
    y: 0.39117,
    widthFrac: 0.0522,
    layouts: {
      desktop: {
        x: 0.7533,
        y: 0.39117,
        widthFrac: 0.0522,
        scale: 1,
      },
      tablet: {
        x: 0.95154,
        y: 0.95261,
        widthFrac: 0.07,
      },
      mobile: {
        x: 0.82198,
        y: 0.5282,
        widthFrac: 0.07,
      },
    },
    sprite: {
      src: `${PROP}/little-pine-tree-v1.png`,
      alt: "Backyard pine tree",
      imageWidth: 500,
      imageHeight: 880,
    },
    footprint: [
      { x: -0.0025, y: 0.001 },
      { x: 0.0025, y: 0.001 },
      { x: 0.002, y: -0.0025 },
      { x: -0.002, y: -0.0025 },
    ],
    stand: { x: 0.008, y: 0.012 },
    hitRadius: 0,
    ambient: "canopy-sway",
    groundShadow: "ellipse",
  },
  {
    id: "tree-left",
    title: "Big Leafy Tree",
    icon: "🌳",
    actionLabel: "Look",
    shape: "hammock",
    interactive: false,
    enabled: true,
    x: 0.4831,
    y: 0.54969,
    widthFrac: 0.1861,
    layouts: {
      desktop: {
        x: 0.4831,
        y: 0.54969,
        widthFrac: 0.1861,
        scale: 1,
      },
      tablet: {
        x: 0.38325,
        y: 0.60623,
        widthFrac: 0.2131,
      },
      mobile: {
        x: 0.4128,
        y: 0.62629,
        widthFrac: 0.1817,
      },
    },
    sprite: {
      src: `${PROP}/tree-left-v1.png`,
      alt: "Large backyard leafy tree",
      imageWidth: 1024,
      imageHeight: 1385,
    },
    footprint: [
      { x: -0.012, y: 0.003 },
      { x: 0.012, y: 0.003 },
      { x: 0.01, y: -0.012 },
      { x: -0.01, y: -0.012 },
    ],
    stand: { x: 0, y: 0.029 },
    hitRadius: 0,
    ambient: "canopy-sway",
    groundShadow: "ellipse",
  },
  {
    id: "swingset-slide",
    title: "Swingset with Slide",
    icon: "🛝",
    actionLabel: "Look",
    shape: "hammock",
    enabled: true,
    x: 0.74008,
    y: 0.62318,
    widthFrac: 0.292,
    flipX: true,
    layouts: {
      desktop: {
        x: 0.74008,
        y: 0.62318,
        widthFrac: 0.292,
        scale: 1,
        flipX: true,
      },
      tablet: {
        x: 0.73913,
        y: 0.62915,
        widthFrac: 0.2682,
      },
      mobile: {
        x: 0.66196,
        y: 0.64428,
        widthFrac: 0.2153,
      },
    },
    sprite: {
      src: `${SHARED_PROP}/swingset-slide-v1.png`,
      alt: "Wooden swingset with two swings and a slide",
      imageWidth: 1430,
      imageHeight: 783,
    },
    footprint: [
      { x: -0.06, y: 0.01 },
      { x: 0.06, y: 0.01 },
      { x: 0.055, y: -0.02 },
      { x: -0.055, y: -0.02 },
    ],
    stand: { x: 0, y: 0.035 },
    hit: { x: 0, y: -0.04 },
    hitRadius: 0.03,
    activationZone: {
      shape: "circle",
      x: 0,
      y: 0.035,
      radius: 0.016,
    },
    groundShadow: "soft",
  },
  {
    id: "gas-grill",
    title: "Gas Grill",
    icon: "🍖",
    actionLabel: "Look",
    shape: "fire-pit",
    enabled: true,
    x: 0.30693,
    y: 0.92419,
    widthFrac: 0.0869,
    layouts: {
      desktop: {
        x: 0.30693,
        y: 0.92419,
        widthFrac: 0.0869,
        scale: 1,
      },
      tablet: {
        x: 0.22072,
        y: 0.96122,
        widthFrac: 0.1124,
      },
      mobile: {
        x: 0.25096,
        y: 0.77979,
        widthFrac: 0.09,
      },
    },
    sprite: {
      src: `${SHARED_PROP}/gas-grill-v1.png`,
      alt: "Freestanding backyard gas grill",
      imageWidth: 1100,
      imageHeight: 1021,
    },
    footprint: [
      { x: -0.025, y: 0.008 },
      { x: 0.025, y: 0.008 },
      { x: 0.022, y: -0.012 },
      { x: -0.022, y: -0.012 },
    ],
    stand: { x: 0, y: 0.03 },
    hit: { x: 0, y: -0.03 },
    hitRadius: 0.02,
    activationZone: {
      shape: "circle",
      x: 0,
      y: 0.03,
      radius: 0.016,
    },
    groundShadow: "soft",
  },
  {
    id: "porch-roof",
    title: "Porch Roof",
    icon: "🏡",
    actionLabel: "Look",
    shape: "hammock",
    interactive: false,
    enabled: true,
    x: 0.42504,
    y: 1,
    widthFrac: 0.6245,
    layouts: {
      desktop: {
        x: 0.42504,
        y: 1,
        widthFrac: 0.6245,
      },
      tablet: {
        x: 0.38211,
        y: 0.98237,
        widthFrac: 0.5702,
      },
      mobile: {
        x: 0.47688,
        y: 0.8389,
        widthFrac: 0.5516,
      },
    },
    sprite: {
      src: `${SHARED_PROP}/porch-roof-under-v1.png`,
      alt: "Under-porch ceiling, front beam, and two posts framing the yard",
      imageWidth: 3100,
      imageHeight: 1043,
    },
    footprint: [],
    stand: { x: 0, y: -0.055 },
    hitRadius: 0,
    occlusion: {
      enabled: true,
      sourceRect: {
        x: -0.3804,
        y: -0.4549,
        width: 0.7607,
        height: 0.4549,
      },
    },
    groundShadow: "soft",
  },
  {
    id: "electric-pizza-oven",
    title: "Electric Pizza Oven",
    icon: "🍕",
    actionLabel: "Look",
    shape: "fire-pit",
    enabled: true,
    x: 0.59091,
    y: 0.91442,
    widthFrac: 0.0534,
    layouts: {
      desktop: {
        x: 0.59091,
        y: 0.91442,
        widthFrac: 0.0534,
        scale: 1,
      },
      tablet: {
        x: 0.46018,
        y: 0.9428,
        widthFrac: 0.065,
        rotationDeg: 0.1,
      },
      mobile: {
        x: 0.6047,
        y: 0.77544,
        widthFrac: 0.0568,
      },
    },
    sprite: {
      src: `${SHARED_PROP}/electric-pizza-oven-v1.png`,
      alt: "Compact outdoor electric pizza oven",
      imageWidth: 900,
      imageHeight: 955,
    },
    footprint: [
      { x: -0.015, y: 0.006 },
      { x: 0.015, y: 0.006 },
      { x: 0.013, y: -0.01 },
      { x: -0.013, y: -0.01 },
    ],
    stand: { x: 0, y: 0.025 },
    hit: { x: 0, y: -0.02 },
    hitRadius: 0.018,
    activationZone: {
      shape: "circle",
      x: 0,
      y: 0.025,
      radius: 0.014,
    },
    groundShadow: "soft",
  },
  {
    id: "holly-springs-large-tree-2",
    title: "Big Leafy Tree 2",
    icon: "🌳",
    actionLabel: "Look",
    shape: "hammock",
    interactive: false,
    enabled: true,
    x: 0.18,
    y: 0.52,
    widthFrac: 0.184,
    layouts: {
      desktop: {
        x: 0.18,
        y: 0.52,
        widthFrac: 0.184,
        scale: 1,
      },
      tablet: {
        x: 0.18,
        y: 0.52,
        widthFrac: 0.18,
        scale: 1,
      },
      mobile: {
        x: 0.16,
        y: 0.55,
        widthFrac: 0.17,
        scale: 1,
      },
    },
    sprite: {
      src: `${PROP}/tree-left-v1.png`,
      alt: "Large backyard leafy tree",
      imageWidth: 1024,
      imageHeight: 1385,
    },
    footprint: [
      { x: -0.012, y: 0.003 },
      { x: 0.012, y: 0.003 },
      { x: 0.01, y: -0.012 },
      { x: -0.01, y: -0.012 },
    ],
    stand: { x: 0, y: 0.029 },
    hitRadius: 0,
    ambient: "canopy-sway",
    groundShadow: "ellipse",
  },
  {
    id: "holly-springs-ac-unit-1",
    title: "AC Unit",
    icon: "❄️",
    actionLabel: "Look",
    shape: "fire-pit",
    interactive: false,
    enabled: true,
    x: 0.9318,
    y: 0.9941,
    widthFrac: 0.0635,
    layouts: {
      desktop: {
        x: 0.9318,
        y: 0.9941,
        widthFrac: 0.0635,
        scale: 1,
      },
      tablet: {
        x: 0.62,
        y: 0.78,
        widthFrac: 0.05,
        scale: 1,
      },
      mobile: {
        x: 0.6,
        y: 0.8,
        widthFrac: 0.048,
        scale: 1,
      },
    },
    sprite: {
      src: `${SHARED_PROP}/ac-unit-v1.png`,
      alt: "Outdoor air conditioner on a concrete pad",
      imageWidth: 811,
      imageHeight: 765,
    },
    footprint: [
      { x: -0.02, y: 0.008 },
      { x: 0.02, y: 0.008 },
      { x: 0.018, y: -0.01 },
      { x: -0.018, y: -0.01 },
    ],
    stand: { x: 0, y: 0.02 },
    hitRadius: 0,
    groundShadow: "soft",
  },
];
